import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Phone, Heart, MessageSquare, Target, Shield, Leaf, ChevronRight } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_clearout-pro/artifacts/px7wjijn_Junk%20Removal%20Logo.webp";

const values = [
  { icon: Heart, title: 'Customer Satisfaction Comes First', description: 'Your happiness with our work is what drives us every day.' },
  { icon: MessageSquare, title: 'Clear Communication', description: 'We keep you informed every step of the way—no surprises.' },
  { icon: Target, title: 'Show Up and Do the Job Right', description: 'We arrive on time and get the job done properly, period.' },
  { icon: Shield, title: 'Respect Your Property', description: 'We treat your home or business like it was our own.' },
  { icon: Leaf, title: 'Eco-Friendly Whenever Possible', description: 'We recycle, reuse, and donate to minimize landfill waste.' },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero with Gradient */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(220,38,38,0.15)_0%,_transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(220,38,38,0.1)_0%,_transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-12">
          <h1 className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-bold text-white mb-6">
            About Junk Removal M.D.
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl">
            Locally owned. Locally operated. Committed to doing right by our neighbors.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-6">
                Your Local Junk Removal Experts
              </h2>
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  Junk Removal M.D. is a locally owned and operated junk removal service based right here in Tahlequah, Oklahoma. We proudly serve Cherokee County and communities throughout Northeast Oklahoma.
                </p>
                <p>
                  What sets us apart? It's simple: we show up when we say we will, we communicate clearly, we price fairly, and we do the job right. Our customers consistently praise our professionalism, responsiveness, and friendly service—and that's exactly how we like it.
                </p>
                <p>
                  Whether you need a single piece of furniture hauled away or a complete property cleanout, we handle every job with the same level of care and efficiency. We serve both residential and commercial customers, treating every property with respect.
                </p>
                <p>
                  We're also committed to doing right by our community and our environment. We recycle, reuse, and donate as much as possible, making sure that usable items find new homes instead of ending up in landfills.
                </p>
                <p>
                  When you work with Junk Removal M.D., you're not just hiring a junk removal service—you're supporting a local business that genuinely cares about doing great work for our neighbors.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-[#1a1a1a] rounded-2xl p-8 md:p-12 flex items-center justify-center min-h-[300px]">
                <img 
                  src={LOGO_URL} 
                  alt="Junk Removal M.D."
                  className="w-full max-w-xs mx-auto"
                />
              </div>
              <div className="mt-4 lg:absolute lg:-bottom-6 lg:-left-6 lg:mt-0 bg-[#dc2626] text-white p-4 lg:p-6 rounded-xl text-center lg:text-left">
                <div className="font-['Barlow_Condensed'] text-base lg:text-lg font-bold uppercase">Based in</div>
                <div className="font-['Barlow_Condensed'] text-2xl lg:text-3xl font-bold">Tahlequah, OK</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 bg-[#f5f5f5]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-4">
              Our Values
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              These are the principles that guide how we work and how we treat every customer.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-[#1a1a1a] p-8 rounded-xl"
                data-testid={`value-${index}`}
              >
                <div className="w-14 h-14 bg-[#dc2626] rounded-xl flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-400">
                  {value.description}
                </p>
              </div>
            ))}
            
            {/* Safe & Efficient Card */}
            <div className="bg-[#1a1a1a] p-8 rounded-xl text-white">
              <div className="w-14 h-14 bg-[#dc2626] rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold mb-3">
                Safe & Efficient Removal
              </h3>
              <p className="text-gray-400">
                Fully insured with both general liability and commercial auto coverage for your complete peace of mind.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Highlight */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="bg-[#1a1a1a] rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white mb-4">
                  Serving Northeast Oklahoma
                </h2>
                <p className="text-gray-400 mb-6">
                  From our home base in Tahlequah, we serve Cherokee County and surrounding areas including Adair, Mayes, Delaware, Craig, Ottawa, Wagoner, Muskogee, and Sequoyah counties.
                </p>
                <ul className="space-y-2">
                  {['Residential junk removal', 'Commercial cleanouts', 'Construction debris', 'Property cleanups'].map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-gray-300">
                      <ChevronRight className="w-5 h-5 text-[#dc2626]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center">
                <div className="inline-block bg-[#dc2626] text-white px-8 py-6 rounded-xl">
                  <div className="font-['Barlow_Condensed'] text-5xl font-bold mb-2">9+</div>
                  <div className="text-sm uppercase tracking-wide">Counties Served</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#dc2626]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-12 text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white mb-6">
            Need Help Clearing Out Junk?
          </h2>
          <p className="text-white/90 text-lg mb-10">
            Whether it's furniture, appliances, debris, or a full cleanout—call today and let's get it handled.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:9183210654">
              <Button className="bg-white text-[#dc2626] hover:bg-gray-100 font-bold uppercase tracking-wider py-6 px-8 btn-press" data-testid="about-call-btn">
                <Phone className="w-5 h-5 mr-2" />
                Call (918) 321-0654
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold uppercase tracking-wider py-6 px-8 btn-press" data-testid="about-estimate-btn">
                Request a Free Estimate
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
